// File: frontend-new/src/pages/LoginPage.js
import React, { useState, useEffect, useContext } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { AuthContext } from '../context/AuthContext'
import logo from '../assets/images/nesim-logo.jpg'
import { FaEye, FaEyeSlash, FaEnvelope, FaSpinner } from 'react-icons/fa'
import './LoginPage.css'

function getPasswordStrength(pw) {
  let score = 0
  if (pw.length >= 8) score++
  if (/[A-Z]/.test(pw)) score++
  if (/[a-z]/.test(pw)) score++
  if (/\d/.test(pw)) score++
  if (/[^A-Za-z0-9]/.test(pw)) score++
  return Math.min(score, 4)
}

export default function LoginPage() {
  const { login } = useContext(AuthContext)
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPwd, setShowPwd] = useState(false)
  const [error, setError] = useState(null)
  const [strength, setStrength] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)

  const baseUrl = process.env.REACT_APP_API_URL || ''

  useEffect(() => {
    setStrength(getPasswordStrength(password))
  }, [password])

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      console.log('→ Sending login:', { email, password })
      const res = await fetch(`${baseUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })

      console.log('← Received status:', res.status, res.statusText)
      let payload = {};
      let textResponse = '';
      let isJson = false;
      const contentType = res.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        try {
          payload = await res.json();
          isJson = true;
          console.log('← Received payload:', payload);
        } catch (parseError) {
          console.log('← Could not parse JSON response');
        }
      } else {
        textResponse = await res.text();
        console.log('← Text response:', textResponse);
      }

      if (!res.ok) {
        let errorMessage = 'Invalid email or password';
        
        if (isJson) {
          // Extract error message defensively from JSON response
          if (payload?.error?.message) {
            errorMessage = payload.error.message;
          } else if (payload?.message) {
            errorMessage = payload.message;
          } else if (payload?.error && typeof payload.error === 'string') {
            errorMessage = payload.error;
          } else if (Array.isArray(payload?.errors) && payload.errors.length > 0) {
            errorMessage = payload.errors[0];
          } else if (payload?.msg) {
            errorMessage = payload.msg;
          }
        } else if (res.status === 404) {
          errorMessage = 'Login service not found (404)';
        } else if (textResponse) {
          errorMessage = textResponse;
        } else if (res.status === 401) {
          errorMessage = 'Invalid email or password';
        } else if (res.status === 400) {
          errorMessage = 'Please check your email and password';
        } else if (res.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
        
        // Ensure errorMessage is always a string
        if (typeof errorMessage !== 'string') {
          errorMessage = 'Login failed. Please try again.';
        }
        
        console.log('🔥 Setting error message:', errorMessage);
        throw new Error(errorMessage);
      }

      const { token, mustChangePassword } = payload;
      if (!token) {
        console.error('❗️ No token in payload:', payload);
        throw new Error('Invalid server response');
      }

      login(token, mustChangePassword);
      navigate(
        mustChangePassword ? '/change-password' : '/',
        { replace: true }
      );
    } catch (err) {
      console.error('🔥 Login error caught:', err);
      
      // Ensure we always have a string error message
      const errorMsg = typeof err.message === 'string' 
        ? err.message 
        : 'Login failed. Please try again.';
      
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: '#fff',
      fontFamily: "'Poppins', sans-serif"
    }}>
      <div className="bg-white rounded-2xl overflow-hidden w-full max-w-md shadow-2xl hover:shadow-3xl transition-all duration-300" style={{ border: '1px solid #ddd' }}>
        {/* Header Section */}
        <div className="p-6 text-center" style={{ background: '#fff', paddingBottom: 0 }}>
          <div className="flex justify-center mb-2">
            <img 
              src={logo}
              alt="NESIM Logo"
              className="h-16 w-auto object-contain"
            />
          </div>
          <div className="mb-1">
            <span style={{ color: '#004080', fontSize: '1rem', fontWeight: 500 }}>Think Brewing solutions, think Nesim</span>
          </div>
        </div>
        {/* Login Form */}
        <div className="p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 text-center">Welcome Back</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-100 border-2 border-red-500 text-red-800 px-4 py-3 rounded-lg text-sm animate-pulse" style={{
                background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                boxShadow: '0 4px 6px -1px rgba(239, 68, 68, 0.1), 0 2px 4px -1px rgba(239, 68, 68, 0.06)'
              }}>
                <div className="flex items-center">
                  <svg className="w-4 h-4 mr-2 flex-shrink-0 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium text-red-900">{error}</span>
                </div>
              </div>
            )}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaEnvelope className="text-gray-400" />
                </div>
                <input 
                  type="email" 
                  id="email"
                  value={email}
                  onChange={e => setEmail(e.target.value.trim())}
                  className="pl-10 w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300" 
                  placeholder="Enter your email" 
                  required
                  autoComplete="username"
                  style={{ borderColor: '#ccc' }}
                  onFocus={e => e.target.style.borderColor = '#004080'}
                  onBlur={e => e.target.style.borderColor = '#ccc'}
                />
              </div>
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {showPwd ? <FaEyeSlash className="text-gray-400" /> : <FaEye className="text-gray-400" />}
                </div>
                <input 
                  type={showPwd ? 'text' : 'password'} 
                  id="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="pl-10 w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300" 
                  placeholder="Enter your password" 
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(v => !v)}
                  className="absolute inset-y-0 left-0 pl-3 flex items-center cursor-pointer text-gray-400 hover:text-gray-600 transition-colors"
                  tabIndex={-1}
                  style={{ background: 'none', border: 'none', padding: 0, width: '2.5rem', height: '100%', zIndex: 2 }}
                >
                  
                </button>
              </div>
              {password && (
                <div className="mt-2">
                  <div className="flex space-x-1">
                    {[0, 1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded ${
                          level <= strength 
                            ? strength === 0 ? 'bg-red-500' :
                              strength === 1 ? 'bg-orange-500' :
                              strength === 2 ? 'bg-yellow-500' :
                              strength === 3 ? 'bg-blue-500' : 'bg-green-500'
                            : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className={`text-xs mt-1 ${
                    strength === 0 ? 'text-red-600' :
                    strength === 1 ? 'text-orange-600' :
                    strength === 2 ? 'text-yellow-600' :
                    strength === 3 ? 'text-blue-600' : 'text-green-600'
                  }`}>
                    {strength === 0 ? 'Too weak' :
                     strength === 1 ? 'Weak' :
                     strength === 2 ? 'Fair' :
                     strength === 3 ? 'Good' : 'Strong'}
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input 
                  id="remember-me" 
                  name="remember-me" 
                  type="checkbox" 
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                  Remember me
                </label>
              </div>
              <div className="text-sm">
                <Link 
                  to="/forgot-password" 
                  className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
            </div>
            <div>
              <button 
                type="submit" 
                disabled={isLoading}
                className="w-full py-3 px-4 rounded-lg font-semibold shadow-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: '#004080',
                  color: '#fff',
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.target.style.background = '#0066cc'
                    e.target.style.color = '#fff'
                    e.target.style.transform = 'translateY(-2px)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    e.target.style.background = '#004080'
                    e.target.style.color = '#fff'
                    e.target.style.transform = 'translateY(0)'
                  }
                }}
              >
                <span style={{color: '#fff'}}>
                  {isLoading ? (
                    <>
                      <FaSpinner className="inline animate-spin mr-2" />
                      Authenticating...
                    </>
                  ) : (
                    'Log In'
                  )}
                </span>
              </button>
            </div>
          </form>
          <div className="mt-8 text-center text-sm text-gray-500">
            Need help?{' '}
            <a href="mailto:IT@nesimltd.co.ke" className="text-blue-600 hover:underline font-medium">Contact IT Support</a>
          </div>
          <div className="text-center text-xs text-gray-500 mt-6">
            &copy; 2025 NESIM Ltd. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  )
}
